import { SlidersHorizontal, X } from "lucide-react";
import { useMemo, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaCombobox } from "@/shared/components/forms/ColocaCombobox";
import { ColocaLabel } from "@/shared/components/forms/ColocaLabel";
import { ColocaSwitch } from "@/shared/components/forms/ColocaSwitch";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";
import { formatRail } from "@/shared/utils/railDisplay";

import { useFeeLabels } from "../../hooks/useFeeLabels";
import {
  PaymentDirection,
  type FeeFiltersState,
  type FeeTabId,
  type ProviderRailRoute,
} from "../../types/paymentFees.types";
import { corridorFilterKey, corridorLabel } from "../../utils/feeFormat";

export interface FeeFiltersProps {
  tab: Exclude<FeeTabId, "corridors">;
  filters: FeeFiltersState;
  routes: ProviderRailRoute[];
  onCorridorChange: (value: string) => void;
  onDirectionChange: (value: PaymentDirection | "") => void;
  onProviderCodeChange: (value: string) => void;
  onShowSupersededChange: (value: boolean) => void;
  onShowScheduledChange: (value: boolean) => void;
  onClear: () => void;
  hasActiveFilters: boolean;
  activeFilterCount: number;
  isMobile: boolean;
}

/**
 * Rule-list filters. Every selector maps to a real query parameter of `GET /internal/payments/fees`, except
 * the two switches — the API cannot filter on a derived status, so `SUPERSEDED`/`SCHEDULED` visibility is
 * client-side.
 *
 * The organization is NOT here: it is the page's scope, not a filter, and lives above this bar so the same
 * pick also decides which price the Corridors tab resolves.
 *
 * Deliberately absent: a text search and a date range (the endpoint supports neither) and an `is_active`
 * toggle (no endpoint can change that flag, so filtering on it would be a control that never does anything).
 *
 * The corridor selector is one pick that drives the three separate `country` / `rail` / `currency` params —
 * they are always coherent in the runtime, so offering them individually would only invite impossible
 * combinations.
 */
export function FeeFilters(props: FeeFiltersProps) {
  const { t } = useTranslations("paymentFees");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const controls = <FeeFilterControls {...props} stacked={props.isMobile} />;

  if (!props.isMobile) {
    return (
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card p-4">
        {controls}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <ColocaButton
        variant="default"
        icon={SlidersHorizontal}
        className="w-full"
        onClick={() => setIsDrawerOpen(true)}
      >
        <span className="flex items-center gap-2">
          {t("filters_button")}
          {props.activeFilterCount > 0 && (
            <ColocaBadge variant="primary" className="font-sans">
              {props.activeFilterCount}
            </ColocaBadge>
          )}
        </span>
      </ColocaButton>

      {/* Active filters stay visible on the page — a collapsed drawer must never hide why a list looks short. */}
      {props.hasActiveFilters && <ActiveFilterChips {...props} />}

      <ColocaDrawer
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        title={t("filters_title")}
        size="sm"
        footer={
          <div className="flex gap-2">
            <ColocaButton variant="default" className="flex-1" onClick={props.onClear}>
              {t("empty_filtered_cta")}
            </ColocaButton>
            <ColocaButton variant="primary" className="flex-1" onClick={() => setIsDrawerOpen(false)}>
              {t("filters_apply")}
            </ColocaButton>
          </div>
        }
      >
        <div className="flex flex-col gap-4">{controls}</div>
      </ColocaDrawer>
    </div>
  );
}

function FeeFilterControls({
  tab,
  filters,
  routes,
  onCorridorChange,
  onDirectionChange,
  onProviderCodeChange,
  onShowSupersededChange,
  onShowScheduledChange,
  onClear,
  hasActiveFilters,
  activeFilterCount,
  stacked,
}: FeeFiltersProps & { stacked: boolean }) {
  const { t } = useTranslations("paymentFees");
  const labels = useFeeLabels();

  const corridorOptions = useMemo(() => {
    const seen = new Map<string, ProviderRailRoute>();
    for (const route of routes) {
      const key = corridorFilterKey(route);
      if (!seen.has(key)) seen.set(key, route);
    }
    return [...seen.entries()].map(([key, route]) => ({
      value: key,
      label: `${route.country} · ${formatRail(route.rail)} · ${route.currency}`,
    }));
  }, [routes]);

  const directionOptions = useMemo(
    () => [
      { value: PaymentDirection.PAYIN, label: labels.direction[PaymentDirection.PAYIN] },
      { value: PaymentDirection.PAYOUT, label: labels.direction[PaymentDirection.PAYOUT] },
    ],
    [labels],
  );

  /**
   * Cost-route options are the distinct provider codes of the catalog, labelled by the corridors they serve —
   * the code itself is the option value and is never rendered (project rule 12).
   */
  const routeOptions = useMemo(() => {
    const byCode = new Map<string, ProviderRailRoute[]>();
    for (const route of routes) {
      const bucket = byCode.get(route.provider_code);
      if (bucket) bucket.push(route);
      else byCode.set(route.provider_code, [route]);
    }
    return [...byCode.entries()].map(([code, corridors]) => {
      const first = corridorLabel(corridors[0], labels.directionShort);
      return {
        value: code,
        label: corridors.length > 1 ? `${first} +${corridors.length - 1}` : first,
      };
    });
  }, [routes, labels]);

  const fieldClass = stacked ? "flex flex-col gap-1.5" : "flex w-56 flex-col gap-1.5";

  return (
    <>
      <div className={fieldClass}>
        <ColocaLabel htmlFor="fee-filter-corridor" className="text-xs text-muted-foreground">
          {t("filter_corridor_label")}
        </ColocaLabel>
        <ColocaCombobox
          id="fee-filter-corridor"
          value={filters.corridor || null}
          onValueChange={(value) => onCorridorChange(value ?? "")}
          options={corridorOptions}
          placeholder={t("filter_corridor_all")}
          aria-label={t("filter_corridor_label")}
        />
      </div>

      <div className={stacked ? "flex flex-col gap-1.5" : "flex w-44 flex-col gap-1.5"}>
        <ColocaLabel htmlFor="fee-filter-direction" className="text-xs text-muted-foreground">
          {t("filter_direction_label")}
        </ColocaLabel>
        <ColocaCombobox
          id="fee-filter-direction"
          value={filters.direction || null}
          onValueChange={(value) => onDirectionChange((value as PaymentDirection) ?? "")}
          options={directionOptions}
          placeholder={t("filter_direction_any")}
          aria-label={t("filter_direction_label")}
        />
      </div>

      {tab === "costs" && (
        <div className={fieldClass}>
          <ColocaLabel htmlFor="fee-filter-route" className="text-xs text-muted-foreground">
            {t("filter_route_label")}
          </ColocaLabel>
          <ColocaCombobox
            id="fee-filter-route"
            value={filters.providerCode || null}
            onValueChange={(value) => onProviderCodeChange(value ?? "")}
            options={routeOptions}
            placeholder={t("filter_route_all")}
            aria-label={t("filter_route_label")}
          />
        </div>
      )}

      <div className="flex flex-col gap-2 text-sm">
        <label className="flex items-center gap-2 text-muted-foreground">
          <ColocaSwitch
            size="sm"
            checked={filters.showSuperseded}
            onCheckedChange={onShowSupersededChange}
            aria-label={t("filter_show_superseded")}
          />
          {t("filter_show_superseded")}
        </label>
        <label className="flex items-center gap-2 text-muted-foreground">
          <ColocaSwitch
            size="sm"
            checked={filters.showScheduled}
            onCheckedChange={onShowScheduledChange}
            aria-label={t("filter_show_scheduled")}
          />
          {t("filter_show_scheduled")}
        </label>
      </div>

      {hasActiveFilters && (
        <ColocaButton variant="ghost" size="sm" onClick={onClear}>
          {t("filter_clear", { count: activeFilterCount })}
        </ColocaButton>
      )}
    </>
  );
}

/** Removable chips for the mobile view, so the active filter set is legible without opening the drawer. */
function ActiveFilterChips({
  tab,
  filters,
  onCorridorChange,
  onDirectionChange,
  onProviderCodeChange,
}: FeeFiltersProps) {
  const { t } = useTranslations("paymentFees");
  const labels = useFeeLabels();

  const chips: { id: string; label: string; onRemove: () => void }[] = [];

  if (filters.corridor) {
    const [country, rail, currency] = filters.corridor.split("|");
    chips.push({
      id: "corridor",
      label: `${country} · ${formatRail(rail)} · ${currency}`,
      onRemove: () => onCorridorChange(""),
    });
  }
  if (filters.direction) {
    chips.push({
      id: "direction",
      label: labels.direction[filters.direction],
      onRemove: () => onDirectionChange(""),
    });
  }
  if (tab === "costs" && filters.providerCode) {
    chips.push({
      id: "route",
      label: t("filter_route_label"),
      onRemove: () => onProviderCodeChange(""),
    });
  }

  return (
    <div className="flex flex-wrap gap-2">
      {chips.map((chip) => (
        <button
          key={chip.id}
          type="button"
          onClick={chip.onRemove}
          className="inline-flex min-h-11 items-center gap-1.5 rounded-full border border-border bg-card px-3 text-xs text-foreground"
        >
          {chip.label}
          <X className="size-3" aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}
